import { useQuery, useMutation } from '@apollo/react-hooks';
import { gql } from 'graphql-tag';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import Badge from 'components/Badge';
import ButtonV2 from 'components/ButtonV2';
import Card from 'components/Card';
import Toaster from 'components/Toaster';
import DeleteConfirmationModal from './DeleteConfirmationModal';

// GraphQL query for fetching ticket templates
const GET_TICKET_TEMPLATES = gql`
  query GetTicketTemplates($account_id: Int!) {
    getTicketTemplates(account_id: $account_id) {
      id
      name
      type
      price
      track_price
      description
      description_video
      description_image_id
      fee_dollar
      fee_percent
      pos_fee_dollar
      pos_fee_percent
      limit
      visibility
      reserved_seat
      reserved_seat_category
      reserved_seat_prefix
      reserved_seat_section
      category_id
      category {
        name
      }
      windows {
        id
        name
        ends
        time
        price
        tiers {
          id
          name
          price
          track_price
        }
      }
      tiers {
        id
        name
        price
        track_price
      }
    }
  }
`;

// GraphQL mutation for deleting a ticket template
const DELETE_TICKET_TEMPLATE = gql`
  mutation DeleteTicketTemplate($id: Int!) {
    deleteTicketTemplate(id: $id)
  }
`;

// Section header
const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const SectionActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SortControl = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: ${({ theme }) => (theme.name === 'dark' ? '#94a3b8' : '#64748b')};
`;

const SortSelect = styled.select`
  font-size: 14px;
  padding: 10px 14px;
  border-radius: 6px;
  border: 1px solid
    ${({ theme }) =>
      theme.name === 'dark' ? 'rgba(255, 255, 255, 0.15)' : '#e2e8f0'};
  background: ${({ theme }) =>
    theme.name === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#ffffff'};
  color: ${({ theme }) => (theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  cursor: pointer;
  outline: none;

  &:focus {
    border-color: #fa4616;
  }
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => (theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ItemCount = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => (theme.name === 'dark' ? '#64748b' : '#94a3b8')};
`;

// Button wrapper for add button
const AddButtonWrapper = styled.div`
  button {
    width: auto;
    padding: 10px 20px;
  }
`;

// Template cards container
const TemplatesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
`;

const TemplateCard = styled.div`
  background: ${({ theme }) =>
    theme?.colors?.card?.background ||
    (theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(255, 255, 255, 0.95)')};
  border: 1px solid
    ${({ theme }) =>
      theme?.colors?.card?.border ||
      (theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgb(226, 232, 240)')};
  border-radius: 12px;
  padding: 20px;
  transition: all 0.2s;

  &:hover {
    border-color: ${({ theme }) =>
      theme.name === 'dark' ? 'rgba(255, 255, 255, 0.2)' : '#cbd5e1'};
    box-shadow: ${({ theme }) =>
      theme.name === 'dark'
        ? '0 4px 12px rgba(0, 0, 0, 0.3)'
        : '0 4px 12px rgba(0, 0, 0, 0.08)'};
  }
`;

const TemplateHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 12px;
`;

const TemplateName = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => (theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0;
  width: 100%;
  word-break: break-word;
`;

const TemplateBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const TemplatePrice = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${({ theme }) => (theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin-bottom: 12px;
`;

const TemplateDetails = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: ${({ theme }) => (theme.name === 'dark' ? '#94a3b8' : '#64748b')};

  svg {
    width: 14px;
    height: 14px;
    opacity: 0.7;
  }
`;

const TemplateActions = styled.div`
  display: flex;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid
    ${({ theme }) =>
      theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgb(226, 232, 240)'};
`;

// Action button wrapper for flex layout
const ActionButtonWrapper = styled.div`
  flex: 1;

  button {
    width: 100%;
    padding: 8px 16px;
    font-size: 13px;
  }
`;

// Empty state
const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
`;

const EmptyStateIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: ${({ theme }) =>
    theme.name === 'dark'
      ? 'rgba(250, 70, 22, 0.15)'
      : 'rgba(250, 70, 22, 0.08)'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  color: #fa4616;

  svg {
    width: 32px;
    height: 32px;
  }
`;

const EmptyStateTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => (theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 8px 0;
`;

const EmptyStateText = styled.p`
  font-size: 14px;
  color: ${({ theme }) => (theme.name === 'dark' ? '#94a3b8' : '#64748b')};
  margin: 0 0 24px 0;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
`;

// Loading state
const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid
    ${({ theme }) =>
      theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.1)'};
  border-top-color: #fa4616;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.p`
  font-size: 14px;
  color: ${({ theme }) => (theme.name === 'dark' ? '#94a3b8' : '#64748b')};
  margin-top: 16px;
`;

// Error state
const ErrorContainer = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: #ef4444;
`;

// Icons
const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const TicketIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" />
    <path d="M13 5v2" />
    <path d="M13 17v2" />
    <path d="M13 11v2" />
  </svg>
);

const EditIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const DollarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="1" x2="12" y2="23" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

const EyeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const formatPrice = (price) => {
  if (price === null || price === undefined || price === '') return '$0.00';
  return `$${parseFloat(price).toFixed(2)}`;
};

const TicketsTab = ({ accountId }) => {
  const history = useHistory();
  const location = useLocation();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [toaster, setToaster] = useState({ isOpen: false });
  const [sortBy, setSortBy] = useState('name_asc');

  // Fetch ticket templates from the API
  const { data, loading, error, refetch } = useQuery(GET_TICKET_TEMPLATES, {
    variables: { account_id: parseInt(accountId) },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });

  // Delete mutation
  const [deleteTicketTemplate] = useMutation(DELETE_TICKET_TEMPLATE, {
    onCompleted: () => {
      refetch();
    },
  });

  const ticketTemplates = data?.getTicketTemplates || [];

  // Determine route prefix for navigation
  const { pathname } = location;
  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  const visibleTemplates = [...ticketTemplates].sort((a, b) => {
    switch (sortBy) {
      case 'name_desc':
        return (b.name || '').localeCompare(a.name || '');
      case 'price_asc':
        return (parseFloat(a.price) || 0) - (parseFloat(b.price) || 0);
      case 'price_desc':
        return (parseFloat(b.price) || 0) - (parseFloat(a.price) || 0);
      case 'name_asc':
      default:
        return (a.name || '').localeCompare(b.name || '');
    }
  });

  const handleAddClick = () => {
    history.push(
      `${routePrefix}/accounts/editAccount/${accountId}/tickets/add`
    );
  };

  const handleEditClick = (template) => {
    history.push({
      pathname: `${routePrefix}/accounts/editAccount/${accountId}/tickets/${template.id}/edit`,
      state: { template },
    });
  };

  const handleDeleteClick = (template) => {
    setSelectedTemplate(template);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedTemplate) return;

    try {
      await deleteTicketTemplate({
        variables: { id: parseInt(selectedTemplate.id) },
      });

      setToaster({
        isOpen: true,
        type: 'success',
        title: 'Template Deleted',
        message: `"${selectedTemplate.name}" has been deleted.`,
      });
    } catch (err) {
      setToaster({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: err.message || 'Failed to delete template.',
      });
    }

    setShowDeleteModal(false);
    setSelectedTemplate(null);
  };

  // Show loading state
  if (loading && !data) {
    return (
      <>
        <SectionHeader>
          <SectionTitle>Ticket Templates</SectionTitle>
          <AddButtonWrapper>
            <ButtonV2
              elevated
              type="button"
              onClick={handleAddClick}
              icon={<PlusIcon />}
            >
              Add Template
            </ButtonV2>
          </AddButtonWrapper>
        </SectionHeader>
        <Card>
          <LoadingContainer>
            <LoadingSpinner />
            <LoadingText>Loading templates...</LoadingText>
          </LoadingContainer>
        </Card>
      </>
    );
  }

  // Show error state
  if (error) {
    return (
      <>
        <SectionHeader>
          <SectionTitle>Ticket Templates</SectionTitle>
          <AddButtonWrapper>
            <ButtonV2
              elevated
              type="button"
              onClick={handleAddClick}
              icon={<PlusIcon />}
            >
              Add Template
            </ButtonV2>
          </AddButtonWrapper>
        </SectionHeader>
        <Card>
          <ErrorContainer>
            <p>Failed to load ticket templates.</p>
            <p>{error.message}</p>
          </ErrorContainer>
        </Card>
      </>
    );
  }

  return (
    <>
      <SectionHeader>
        <SectionTitle>
          Ticket Templates
          {visibleTemplates.length > 0 && (
            <ItemCount>({visibleTemplates.length})</ItemCount>
          )}
        </SectionTitle>
        <SectionActions>
          {visibleTemplates.length > 1 && (
            <SortControl>
              Sort:
              <SortSelect
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="name_asc">Name A–Z</option>
                <option value="name_desc">Name Z–A</option>
                <option value="price_asc">Price Low–High</option>
                <option value="price_desc">Price High–Low</option>
              </SortSelect>
            </SortControl>
          )}
          <AddButtonWrapper>
            <ButtonV2
              elevated
              type="button"
              onClick={handleAddClick}
              icon={<PlusIcon />}
            >
              Add Template
            </ButtonV2>
          </AddButtonWrapper>
        </SectionActions>
      </SectionHeader>

      {visibleTemplates.length === 0 ? (
        <Card>
          <EmptyState>
            <EmptyStateIcon>
              <TicketIcon />
            </EmptyStateIcon>
            <EmptyStateTitle>No Ticket Templates</EmptyStateTitle>
            <EmptyStateText>
              Create ticket templates to use as starting points when adding
              tickets to events. Templates save time by pre-filling common
              ticket configurations.
            </EmptyStateText>
          </EmptyState>
        </Card>
      ) : (
        <TemplatesGrid>
          {visibleTemplates.map((template) => (
            <TemplateCard key={template.id}>
              <TemplateHeader>
                <TemplateName>{template.name}</TemplateName>
                <TemplateBadges>
                  {template.category?.name && (
                    <Badge variant="neutral" size="small">
                      {template.category.name}
                    </Badge>
                  )}
                  <Badge size="small">
                    {template.type
                      ? template.type.charAt(0).toUpperCase() +
                        template.type.slice(1)
                      : 'Ticket'}
                  </Badge>
                </TemplateBadges>
              </TemplateHeader>

              <TemplatePrice>{formatPrice(template.price)}</TemplatePrice>

              <TemplateDetails>
                {template.track_price && (
                  <DetailItem>
                    <DollarIcon />
                    At Track: {formatPrice(template.track_price)}
                  </DetailItem>
                )}
                <DetailItem>
                  <EyeIcon />
                  {template.visibility === 'hidden' ? 'Hidden' : 'Visible'}
                </DetailItem>
              </TemplateDetails>

              <TemplateActions>
                <ActionButtonWrapper>
                  <ButtonV2
                    type="button"
                    onClick={() => handleEditClick(template)}
                    icon={<EditIcon />}
                  >
                    Edit
                  </ButtonV2>
                </ActionButtonWrapper>
                <ActionButtonWrapper>
                  <ButtonV2
                    danger
                    type="button"
                    onClick={() => handleDeleteClick(template)}
                    icon={<TrashIcon />}
                  >
                    Delete
                  </ButtonV2>
                </ActionButtonWrapper>
              </TemplateActions>
            </TemplateCard>
          ))}
        </TemplatesGrid>
      )}

      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedTemplate(null);
        }}
        onConfirm={handleDeleteConfirm}
        templateName={selectedTemplate?.name || ''}
      />

      <Toaster
        isOpen={toaster.isOpen}
        onClose={() => setToaster({ isOpen: false })}
        type={toaster.type}
        title={toaster.title}
        message={toaster.message}
        duration={5000}
      />
    </>
  );
};

TicketsTab.propTypes = {
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
};

export default TicketsTab;
